import { useState, useEffect } from 'react';
import { MessageCircle, Send, Loader2, Trash2, Calendar } from 'lucide-react';
import portfolioCommentService from '../../services/api/portfolioComment.service';
import { toast } from 'react-hot-toast';
import { getImageUrl } from '../../utils/imageUtils';

const CommentsQuestions = ({ portfolioUserId, isOwnProfile = false }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentMode, setCommentMode] = useState('comment');
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    if (portfolioUserId) {
      fetchComments();
    }
  }, [portfolioUserId]);

  const fetchComments = async () => {
    if (!portfolioUserId) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const response = await portfolioCommentService.getPortfolioComments(portfolioUserId);
      console.log('Fetch comments response:', response);
      if (response.success) {
        console.log('Comments data:', response.data);
        setComments(response.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch comments:', error);
      setComments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitComment = async () => {
    if (!commentText.trim()) {
      toast.error('Please enter a comment or question');
      return;
    }

    if (commentText.length > 2000) {
      toast.error('Comment must be less than 2000 characters');
      return;
    }

    try {
      setSubmitting(true);
      const response = await portfolioCommentService.createComment(
        portfolioUserId,
        commentMode,
        commentText.trim()
      );

      if (response.success) {
        toast.success(response.message || `${commentMode === 'question' ? 'Question' : 'Comment'} posted successfully`);
        setCommentText('');
        await fetchComments();
      }
    } catch (error) {
      console.error('Failed to post comment:', error);
      toast.error(error.response?.data?.message || 'Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) {
      return;
    }

    try {
      setDeletingId(commentId);
      const response = await portfolioCommentService.deleteComment(commentId);
      if (response.success) {
        toast.success('Comment deleted successfully');
        setComments(comments.filter(c => c.id !== commentId));
      }
    } catch (error) {
      console.error('Failed to delete comment:', error);
      toast.error('Failed to delete comment');
    } finally {
      setDeletingId(null);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || 'U';
  };

  const getBadgeColor = (type) => {
    return type === 'question' 
      ? 'bg-blue-100 text-blue-700 border-blue-200'
      : 'bg-orange-100 text-orange-700 border-orange-200';
  };

  const getAvatarGradient = (index) => {
    const gradients = [
      'from-purple-400 to-purple-600',
      'from-green-400 to-green-600',
      'from-blue-400 to-blue-600',
      'from-pink-400 to-pink-600',
      'from-yellow-400 to-yellow-600',
      'from-red-400 to-red-600'
    ];
    return gradients[index % gradients.length];
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center bg-red-50 border border-red-200 flex-shrink-0">
            <MessageCircle className="w-4 h-4 text-red-600" />
          </div>
          <h2 className="text-base font-bold text-gray-900">
            Comments & Questions
          </h2>
          {comments.length > 0 && (
            <span className="w-6 h-6 bg-red-50 border border-red-200 rounded-full flex items-center justify-center text-xs font-bold text-red-600">
              {comments.length}
            </span>
          )}
        </div>
      </div>

      {!isOwnProfile && (
        <div className="mb-6">
          <div className="flex gap-2 mb-3">
            {['comment', 'question'].map((mode) => (
              <button
                key={mode}
                onClick={() => setCommentMode(mode)}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all capitalize ${
                  commentMode === mode
                    ? 'bg-red-600 text-white shadow-sm'
                    : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
                }`}
              >
                {mode}
              </button>
            ))}
          </div>
          <div className="relative">
            <textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder={commentMode === 'comment'
                ? 'Leave a comment about what you like…'
                : 'Ask a question…'}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 resize-none transition-colors"
              rows={3}
              maxLength={2000}
            />
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-gray-400">{commentText.length} / 2000</span>
              <button
                onClick={handleSubmitComment}
                disabled={submitting || !commentText.trim()}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Send className="w-3 h-3" />
                )}
                Post
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8">
          <Loader2 className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-2" />
          <p className="text-sm text-gray-500">Loading comments...</p>
        </div>
      ) : comments.length === 0 ? (
        <div className="text-center py-8">
          <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">
            {isOwnProfile 
              ? 'No comments or questions yet'
              : 'Be the first to comment or ask a question'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {comments.map((comment, index) => (
            <div key={comment.id} className="flex gap-3 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
              <div className="flex-shrink-0">
                {comment.commenter?.profilePicture ? (
                  <img
                    src={getImageUrl(comment.commenter.profilePicture)}
                    alt={`${comment.commenter.firstName} ${comment.commenter.lastName}`}
                    className="w-8 h-8 rounded-full object-cover border-2 border-gray-200"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div
                  className={`w-8 h-8 bg-gradient-to-br ${getAvatarGradient(index)} rounded-full flex items-center justify-center`}
                  style={{ display: comment.commenter?.profilePicture ? 'none' : 'flex' }}
                >
                  <span className="text-xs font-bold text-white">
                    {getInitials(comment.commenter?.firstName, comment.commenter?.lastName)}
                  </span>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span className="text-sm font-bold text-gray-900">
                    {comment.commenter?.firstName} {comment.commenter?.lastName}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getBadgeColor(comment.type)}`}>
                    {comment.type}
                  </span>
                  <span className="text-xs text-gray-400">{formatTime(comment.createdAt)}</span>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">{comment.content}</p>
              </div>
              {(isOwnProfile || comment.commenterId === comment.commenter?.id) && (
                <button
                  onClick={() => handleDeleteComment(comment.id)}
                  disabled={deletingId === comment.id}
                  className="flex-shrink-0 p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                >
                  {deletingId === comment.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CommentsQuestions;
